import { SITE, productKey } from "@/lib/site";

export function AppStoreBadge({ product = "ember", className = "" }) {
  const key = productKey(product);
  const href = SITE.products[key]?.appStoreUrl || SITE.appStoreUrl;
  const external = href && href !== "#";
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      aria-label={`Download ${SITE.products[key].name} on the App Store`}
      data-testid={`${product}-app-store-badge`}
      className={`group inline-flex items-center gap-3 rounded-xl bg-[#0A1128] text-white pl-3 pr-4 py-2.5 hover:bg-[#1E293B] transition-colors ${className}`}
    >
      <svg width="22" height="26" viewBox="0 0 24 28" fill="currentColor" aria-hidden>
        <path d="M17.05 14.84c-.03-3.15 2.57-4.66 2.69-4.74-1.46-2.13-3.74-2.42-4.55-2.46-1.94-.2-3.78 1.14-4.77 1.14-.99 0-2.5-1.11-4.11-1.08-2.12.03-4.08 1.23-5.17 3.12-2.2 3.82-.56 9.46 1.59 12.55 1.05 1.51 2.3 3.21 3.94 3.15 1.58-.06 2.18-1.02 4.09-1.02 1.91 0 2.45 1.02 4.13.99 1.71-.03 2.79-1.54 3.83-3.07 1.21-1.76 1.71-3.47 1.74-3.55-.04-.02-3.34-1.28-3.37-5.07Zm-3.13-9.31c.87-1.06 1.46-2.53 1.3-4-1.25.05-2.78.83-3.68 1.89-.8.94-1.51 2.45-1.32 3.89 1.4.11 2.83-.71 3.7-1.78Z" />
      </svg>
      <span className="flex flex-col leading-none">
        <span className="text-[10px] uppercase tracking-wider text-slate-300">Download on the</span>
        <span className="font-display text-[15px] tracking-tight mt-0.5">App Store</span>
      </span>
    </a>
  );
}

export function GooglePlayBadge({ product = "ember", className = "" }) {
  const key = productKey(product);
  const href = SITE.products[key]?.playStoreUrl || SITE.playStoreUrl;
  const external = href && href !== "#";
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      aria-label={`Get ${SITE.products[key].name} on Google Play`}
      data-testid={`${product}-google-play-badge`}
      className={`group inline-flex items-center gap-3 rounded-xl bg-[#0A1128] text-white pl-3 pr-4 py-2.5 hover:bg-[#1E293B] transition-colors ${className}`}
    >
      <svg width="22" height="26" viewBox="0 0 24 27" aria-hidden>
        <defs>
          <linearGradient id={`gp-a-${product}`} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#00C9FF" />
            <stop offset="1" stopColor="#2EB6FF" />
          </linearGradient>
          <linearGradient id={`gp-b-${product}`} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#FFD400" />
            <stop offset="1" stopColor="#FF9100" />
          </linearGradient>
          <linearGradient id={`gp-c-${product}`} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#FF3B47" />
            <stop offset="1" stopColor="#E62B3A" />
          </linearGradient>
          <linearGradient id={`gp-d-${product}`} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#00D26A" />
            <stop offset="1" stopColor="#00A352" />
          </linearGradient>
        </defs>
        <path d="M3 1.4v24.2c0 .6.6 1 1.1.7L17 14.1 4.1.7C3.6.4 3 .8 3 1.4Z" fill={`url(#gp-a-${product})`} />
        <path d="M21 11.6 17 14.1 21 16.6c.8.5.8 1.7 0 2.2L17 21 4.1 26.6c-.5.3-1.1-.1-1.1-.7L17 14.1 21 11.6Z" fill={`url(#gp-c-${product})`} />
        <path d="M17 14.1 4.1 26.6c-.5.3-1.1-.1-1.1-.7V14.1L17 14.1Z" fill={`url(#gp-d-${product})`} />
        <path d="M21 11.6c.8.5.8 1.7 0 2.2L17 14.1l4-2.5Z" fill={`url(#gp-b-${product})`} />
      </svg>
      <span className="flex flex-col leading-none">
        <span className="text-[10px] uppercase tracking-wider text-slate-300">Get it on</span>
        <span className="font-display text-[15px] tracking-tight mt-0.5">Google Play</span>
      </span>
    </a>
  );
}

export default function StoreBadges({
  product = "ember",
  align = "start",
  className = "",
}) {
  const alignCls = align === "center" ? "justify-center" : "";
  return (
    <div
      data-testid={`${product}-store-badges`}
      className={`flex flex-wrap items-center gap-3 ${alignCls} ${className}`}
    >
      <AppStoreBadge product={product} />
      <GooglePlayBadge product={product} />
    </div>
  );
}
